import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import styles from "../../../styles/misreports/misreport.module.css";
import coverStyles from "./GhgReportCover.module.css";
import {
  getGhgReportPdfStyles,
  getGhgReportFirstPagePdfCss,
  GHG_PDF_BACKEND_MARGINS,
} from "./ghgReportPdfStyles";
import GhgReportStyledPages from "./GhgReportStyledPages";
import EditableReportSection from "./EditableReportSection";
import companyLogo from "../../../assets/images/nirantara_logo_new.svg";
// companyLogo above stays Nirantara-branded — it's the fallback for the
// CLIENT's own logo (see companyLogoSrc || companyLogo below), unrelated to
// the "Powered by" footer. poweredByLogo is the "Powered by" asset itself.
import poweredByLogo from "../../../assets/images/vajra_powered_by_logo.svg";
import ghgCoverBg from "../../../assets/Report/ghg.jpg";
import vajraLogo from "../../../assets/Report/Vajralogo.jpg";
import { useUser } from "../../../context/AuthContext";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { get, put } from "../../../utils/api";
import { truncateEmissionNumber } from "../../../utils/common";
import { api } from "../../../utils/api";
import { vajraLogoTranparentImg } from "../../../utils/constant";
import { getConsultantLogoUrl } from "../../../utils/consultantLogo";

const GHG_TABLE_OF_CONTENTS = [
  "Executive Summary",
  "1. GENERAL Information",
  "2. Organizational Boundary",
  "3. Operational Boundary",
  "4. Emission Factors & Data Sources",
  "5. GHG Quantification Methodology",
  "6. GHG Data Aggregation And Reporting Period",
  "7. GHG Reporting",
  "8. Assumptions",
  "9. Limitations",
  "10. Analysis",
  "11. Verification And Validation",
  "12. Conclusions",
  "Appendices",
];

/**
 * Word ignores most CSS on data-uri images but honors width/height.
 * Chart.js canvases are often 2–3× layout size — downscale so the .doc fits the page.
 */
function chartCanvasForWordExport(sourceCanvas, maxWidthPx = 640) {
  if (!(sourceCanvas instanceof HTMLCanvasElement)) return null;
  const sw = sourceCanvas.width;
  const sh = sourceCanvas.height;
  if (!sw || !sh) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: Math.max(sw, 1),
        height: Math.max(sh, 1),
      };
    } catch {
      return null;
    }
  }
  const scale = Math.min(1, maxWidthPx / sw);
  const dw = Math.max(1, Math.round(sw * scale));
  const dh = Math.max(1, Math.round(sh * scale));
  if (scale >= 1) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: sw,
        height: sh,
      };
    } catch {
      return null;
    }
  }
  const out = document.createElement("canvas");
  out.width = dw;
  out.height = dh;
  const ctx = out.getContext("2d");
  if (!ctx) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: sw,
        height: sh,
      };
    } catch {
      return null;
    }
  }
  ctx.drawImage(sourceCanvas, 0, 0, sw, sh, 0, 0, dw, dh);
  try {
    return { dataUrl: out.toDataURL("image/png"), width: dw, height: dh };
  } catch {
    return null;
  }
}

const GhgReport = () => {
  const PageBreak = () => (
    <div
    // className={styles.page_break}
    // style={{ pageBreakBefore: "always", breakBefore: "page" }}
    />
  );

  const reportRef = useRef();
  const { user } = useUser();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const reportYear = searchParams.get("year");
  const shouldRegenerateOnOpen = useRef(location.state?.regenerate === true);
  const didLoadReportOnOpen = useRef(false);
  const [loading, setLoading] = useState(false); // State to track loading

  const [data, setData] = useState({});
  const [scope1BarChartData, setScope1BarChartData] = useState([]);
  const [scope2BarChartData, setScope2BarChartData] = useState([]);
  const [scope3BarChartData, setScope3BarChartData] = useState([]);
  const [summary, setSummary] = useState({
    scope1: "",
    scope2: "",
    scope3: "",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [waterData, setWaterData] = useState([]);
  const [executiveSummaryText, setExecutiveSummaryText] = useState("");
  const [executiveSummaryOriginal, setExecutiveSummaryOriginal] = useState("");
  const [organizationalBoundaryText, setOrganizationalBoundaryText] =
    useState("");
  const [organizationalBoundaryOriginal, setOrganizationalBoundaryOriginal] =
    useState("");
  const [operationalBoundaryText, setOperationalBoundaryText] = useState("");
  const [operationalBoundaryOriginal, setOperationalBoundaryOriginal] =
    useState("");
  const [emissionFactorsText, setEmissionFactorsText] = useState("");
  const [emissionFactorsOriginal, setEmissionFactorsOriginal] = useState("");
  const [quantificationMethodologyText, setQuantificationMethodologyText] =
    useState("");
  const [
    quantificationMethodologyOriginal,
    setQuantificationMethodologyOriginal,
  ] = useState("");
  const [dataAggregationText, setDataAggregationText] = useState("");
  const [dataAggregationOriginal, setDataAggregationOriginal] = useState("");
  const [assumptionsText, setAssumptionsText] = useState("");
  const [assumptionsOriginal, setAssumptionsOriginal] = useState("");
  const [limitationsText, setLimitationsText] = useState("");
  const [limitationsOriginal, setLimitationsOriginal] = useState("");
  const [analysisText, setAnalysisText] = useState("");
  const [analysisOriginal, setAnalysisOriginal] = useState("");
  const [verificationText, setVerificationText] = useState("");
  const [verificationOriginal, setVerificationOriginal] = useState("");
  const [conclusionsText, setConclusionsText] = useState("");
  const [conclusionsOriginal, setConclusionsOriginal] = useState("");
  const fetchGhgReportData = async (endpoint, { showLoader = false } = {}) => {
    if (!id) return null;

    try {
      if (showLoader) {
        setLoading(true);
      }
      const response = await get(`${endpoint}?id=${id}`);
      const reportData = response?.data?.data;
      console.log("reportData", reportData);
      setData(reportData);
      return reportData;
    } catch (error) {
      console.log("error", error);
      return null;
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const getData = () => fetchGhgReportData("/ghg-report/data");

  const regenerate = () =>
    fetchGhgReportData("/ghg-report/regenerate", { showLoader: true });

  const getGHGFacilityWiseData = async () => {
    try {
      const response = await get(`/ghg-report/ghg?id=${id}`);
      setScope1BarChartData(response?.data?.scope1);
      setScope2BarChartData(response?.data?.scope2);
      setScope3BarChartData(response?.data?.scope3);
      setSummary({
        scope1: response?.data?.scope1Summary,
        scope2: response?.data?.scope2Summary,
        scope3: response?.data?.scope3Summary,
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (didLoadReportOnOpen.current) return;
    didLoadReportOnOpen.current = true;

    if (shouldRegenerateOnOpen.current) {
      shouldRegenerateOnOpen.current = false;
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
      regenerate();
    } else {
      getData();
    }
    getGHGFacilityWiseData();
  }, []);

  const getInventoryYearLabel = (year, isFiscal, isPrevious = false) => {
    const y = Number(year || new Date().getFullYear());
    const targetYear = isPrevious ? y - 1 : y;
    if (isFiscal) {
      const nextShort = String(targetYear + 1).slice(-2);
      return `${targetYear}-${nextShort}`;
    }
    return `${targetYear}`;
  };

  const isFiscalYear = data?.reportingPeriod === "FiscalYear";
  const currentYearDisplay = getInventoryYearLabel(
    data?.reportingYear,
    isFiscalYear,
  );
  const previousYearDisplay = getInventoryYearLabel(
    data?.reportingYear,
    isFiscalYear,
    true,
  );
  const currentYearLabel = `${currentYearDisplay} Inventory Year (tCO2e)`;
  const previousYearLabel = `${previousYearDisplay} Inventory Year (tCO2e)`;

  const companyLogoSrc = user?.groupManager
    ? user?.groupCompanyId?.image
    : user?.company?.companyImage;

  // White-label: when this client was onboarded by a consultant who has
  // uploaded a logo, every "Vajra"/platform logo slot in the report (cover,
  // section headers) shows the consultant's logo instead. The client's own
  // company logo slot is untouched. Falls back to the bundled Vajra assets.
  const consultantLogoUrl = getConsultantLogoUrl();
  const platformLogo = consultantLogoUrl || vajraLogo;
  const platformLogoTransparent = consultantLogoUrl || vajraLogoTranparentImg;

  const getReportingPeriodLabel = () => {
    if (data?.reportingPeriod === "CalendarYear") {
      return `${data?.reportingYear ?? "YYYY"}`;
    }
    if (data?.reportingPeriod === "FiscalYear") {
      return `Apr ${data?.reportingYear ?? "YYYY"} - Mar ${(data?.reportingYear ?? 0) + 1 || "YYYY"}`;
    }
    if (reportYear) {
      return `Apr ${reportYear} - Mar ${Number(reportYear) + 1}`;
    }
    return "Apr 2021 - Mar 2022";
  };

  const getReportDateLabel = () => {
    const date = data?.updatedAt ? new Date(data.updatedAt) : new Date();
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const companyName = user?.company?.companyName || "Nirantara Ecoventures";

  const getTotalEmissions = () =>
    (data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0) +
    (data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0) +
    (data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0);

  const scope1Total =
    data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 154980.7;
  const scope2Total = data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0;
  const scope3Total =
    data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 145340.35;
  const scope1Change =
    data?.scope1Emissions?.totalScope1Combustion?.percentageChange ?? 100;
  const scope2Change =
    data?.scope2Emissions?.totalEmission?.percentageChange ?? 0;
  const scope3Change =
    data?.scope3Emissions?.totalEmission?.percentageChange ?? 100;

  const defaultExecutiveSummaryText = useMemo(() => {
    if (data?.executiveSummary?.overallPerformance) {
      return data.executiveSummary.overallPerformance;
    }

    const year = data?.reportingYear || new Date().getFullYear();
    const total = truncateEmissionNumber(getTotalEmissions());
    const s1 = truncateEmissionNumber(scope1Total);
    const s2 = truncateEmissionNumber(scope2Total);
    const s3 = truncateEmissionNumber(scope3Total);

    return `In ${year}, ${companyName} reported an annual greenhouse gas emissions totaling ${total} tCO2e. The breakdown of emissions reveals that Scope 1 emissions amounted to ${s1} tCO2e, while Scope 2 emissions stand at ${s2} tCO2e, largely due to an absence of electricity, heat, and steam consumption. Scope 3 emissions reflect contributions mainly from upstream and downstream activities, amounting to ${s3} tCO2e. Notably, Scope 3 emissions represent a substantial portion of the total, marking these activities as key emission drivers for the company, and implying no emissions under several categories such as employee commute and purchased goods.`;
  }, [
    data?.executiveSummary?.overallPerformance,
    data?.reportingYear,
    companyName,
    scope1Total,
    scope2Total,
    scope3Total,
  ]);

  const getExecutiveSummaryText = () =>
    executiveSummaryText || defaultExecutiveSummaryText;

  const reportingPeriodLabel = getReportingPeriodLabel();

  const defaultOrganizationalBoundaryText = useMemo(() => {
    if (data?.executiveSummary?.organizationalBoundary) {
      return data.executiveSummary.organizationalBoundary;
    }
    if (data?.organizationalBoundaryDescription) {
      return data.organizationalBoundaryDescription;
    }
    if (data?.organizationalBoundaryText) {
      return data.organizationalBoundaryText;
    }

    return `The organizational boundary for greenhouse gas (GHG) emissions accounting for ${companyName} for the reporting period ${reportingPeriodLabel} is determined in accordance with the GHG Protocol Corporate Standard. ${companyName} applies the operational control approach to define its organizational boundary. The following entities and activities are included within the organizational boundary:`;
  }, [
    data?.executiveSummary?.organizationalBoundary,
    data?.organizationalBoundaryDescription,
    data?.organizationalBoundaryText,
    companyName,
    reportingPeriodLabel,
  ]);

  const defaultOperationalBoundaryText = useMemo(() => {
    if (data?.executiveSummary?.operationalBoundary) {
      return data.executiveSummary.operationalBoundary;
    }
    if (
      typeof data?.operationalBoundary === "string" &&
      data.operationalBoundary.trim()
    ) {
      return data.operationalBoundary;
    }
    if (data?.operationalBoundaryDescription) {
      return data.operationalBoundaryDescription;
    }
    if (data?.operationalBoundaryText) {
      return data.operationalBoundaryText;
    }

    return `${companyName} applies the operational control approach to establish its operational boundary. The operational boundary defines the direct and indirect emissions associated with operations over which the company has operational control. The operational boundary includes direct operational activities (Scope 1 and Scope 2) and material Scope 3 value chain activities including purchased goods and services, waste generated in operations, business travel, employee commuting, and upstream and downstream transportation and distribution.`;
  }, [
    data?.executiveSummary?.operationalBoundary,
    data?.operationalBoundary,
    data?.operationalBoundaryDescription,
    data?.operationalBoundaryText,
    companyName,
  ]);

  const defaultEmissionFactorsText = useMemo(() => {
    if (data?.executiveSummary?.emissionFactorAndDataSource) {
      return data.executiveSummary.emissionFactorAndDataSource;
    }
    if (data?.emissionFactorAndDataSource) {
      return data.emissionFactorAndDataSource;
    }
    if (data?.ghgEmissionFactor) {
      return data.ghgEmissionFactor;
    }

    return "";
  }, [
    data?.executiveSummary?.emissionFactorAndDataSource,
    data?.emissionFactorAndDataSource,
    data?.ghgEmissionFactor,
  ]);

  const defaultQuantificationMethodologyText = useMemo(() => {
    if (data?.executiveSummary?.ghgQuntificationMethodology) {
      return data.executiveSummary.ghgQuntificationMethodology;
    }
    if (data?.executiveSummary?.ghgQuantificationMethodology) {
      return data.executiveSummary.ghgQuantificationMethodology;
    }
    if (data?.executiveSummary?.quantificationMethodology) {
      return data.executiveSummary.quantificationMethodology;
    }
    if (data?.ghgQuntificationMethodology) {
      return data.ghgQuntificationMethodology;
    }
    if (data?.ghgQuantificationMethodology) {
      return data.ghgQuantificationMethodology;
    }
    if (data?.quantificationMethodology) {
      return data.quantificationMethodology;
    }

    return "";
  }, [
    data?.executiveSummary?.ghgQuntificationMethodology,
    data?.executiveSummary?.ghgQuantificationMethodology,
    data?.executiveSummary?.quantificationMethodology,
    data?.ghgQuntificationMethodology,
    data?.ghgQuantificationMethodology,
    data?.quantificationMethodology,
  ]);

  const defaultDataAggregationText = useMemo(() => {
    if (data?.executiveSummary?.ghgDataAggregationAdnReportingPeriod) {
      return data.executiveSummary.ghgDataAggregationAdnReportingPeriod;
    }
    if (data?.executiveSummary?.ghgDataAggregationAndReportingPeriod) {
      return data.executiveSummary.ghgDataAggregationAndReportingPeriod;
    }
    if (data?.executiveSummary?.ghgDataAggregation) {
      return data.executiveSummary.ghgDataAggregation;
    }
    if (data?.executiveSummary?.dataAggregationAndReportingPeriod) {
      return data.executiveSummary.dataAggregationAndReportingPeriod;
    }
    if (data?.ghgDataAggregationAdnReportingPeriod) {
      return data.ghgDataAggregationAdnReportingPeriod;
    }
    if (data?.ghgDataAggregationAndReportingPeriod) {
      return data.ghgDataAggregationAndReportingPeriod;
    }
    if (data?.ghgDataAggregation) {
      return data.ghgDataAggregation;
    }
    if (data?.dataAggregationAndReportingPeriod) {
      return data.dataAggregationAndReportingPeriod;
    }

    return "";
  }, [
    data?.executiveSummary?.ghgDataAggregationAdnReportingPeriod,
    data?.executiveSummary?.ghgDataAggregationAndReportingPeriod,
    data?.executiveSummary?.ghgDataAggregation,
    data?.executiveSummary?.dataAggregationAndReportingPeriod,
    data?.ghgDataAggregationAdnReportingPeriod,
    data?.ghgDataAggregationAndReportingPeriod,
    data?.ghgDataAggregation,
    data?.dataAggregationAndReportingPeriod,
  ]);

  const defaultAssumptionsText = useMemo(() => {
    if (typeof data?.assumptions === "string" && data.assumptions.trim()) {
      return data.assumptions;
    }
    if (Array.isArray(data?.assumptions) && data.assumptions.length) {
      return data.assumptions.join;
    }

    return [
      "No material assumptions were recorded for the reporting period. Users may update this section with any organisation-specific assumptions before finalising the report.",
    ].join("\n");
  }, [data?.assumptions]);

  const defaultLimitationsText = useMemo(() => {
    if (data?.limitations) {
      return data.limitations;
    }

    return "No material limitations were recorded for the reporting period. Users may update this section with any organisation-specific limitations before finalising the report.";
  }, [data?.limitations]);

  const defaultAnalysisText = useMemo(() => {
    const existingAnalysis =
      data?.executiveSummary?.analysis || data?.analysis || data?.analysisText;

    if (typeof existingAnalysis === "string" && existingAnalysis.trim()) {
      return existingAnalysis;
    }

    const analysisSections =
      data?.executiveSummary?.analysisSections || data?.analysisSections;

    if (Array.isArray(analysisSections) && analysisSections.length) {
      return analysisSections
        .map((section, index) => {
          if (typeof section === "string") return section;

          const title =
            section?.title ||
            section?.heading ||
            section?.name ||
            `Analysis ${index + 1}`;
          const content =
            section?.content ||
            section?.description ||
            section?.text ||
            section?.value ||
            "";

          return `${title}\n${content}`.trim();
        })
        .filter(Boolean)
        .join("\n\n");
    }

    return [
      `1. Emission Composition\nScope 1 emissions contribute ${truncateEmissionNumber(data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0)} tCO2e, Scope 2 contributes ${truncateEmissionNumber(data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0)} tCO2e, and Scope 3 contributes ${truncateEmissionNumber(data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0)} tCO2e to the total inventory of ${truncateEmissionNumber(getTotalEmissions())} tCO2e.`,
      "2. Hotspot Concentration\nThe highest contributing emission sources were identified by comparing category-level totals across Scope 1, Scope 2, and Scope 3 to prioritize reduction opportunities.",
      `3. Trend Analysis\nYear-on-year changes were assessed by comparing ${currentYearDisplay} inventory year data with ${previousYearDisplay} where historical records were available.`,
      "4. Intensity Analysis\nEmission intensity metrics such as emissions per unit of production or per employee were reviewed where relevant operational data was available.",
      "5. Boundary and Data Quality Interpretation\nResults were interpreted within the defined organizational and operational boundaries, with data quality reviewed for completeness and consistency.",
      "6. Scope 4 Separation\nAvoided emissions (Scope 4) are reported separately from the main GHG inventory and are not included in the total emissions figure presented in this report.",
    ].join("\n\n");
  }, [
    data?.executiveSummary?.analysis,
    data?.analysis,
    data?.analysisText,
    data?.executiveSummary?.analysisSections,
    data?.analysisSections,
    data?.scope1Emissions,
    data?.scope2Emissions,
    data?.scope3Emissions,
    currentYearDisplay,
    previousYearDisplay,
  ]);

  const defaultVerificationText = useMemo(() => {
    return (
      data?.verificationAndValidation ||
      data?.verification ||
      "The inventory was not externally verified for the reporting period."
    );
  }, [data?.verificationAndValidation, data?.verification]);

  const defaultConclusionsText = useMemo(() => {
    return (
      data?.executiveSummary?.summaryOfFindings ||
      `In ${data?.reportingYear || new Date().getFullYear()}, ${companyName} reported total GHG emissions of ${truncateEmissionNumber(getTotalEmissions())} tCO2e. Scope 3 represents a substantial share of the overall inventory, indicating value chain activities as key emission drivers. Scope 1 and Scope 2 contributions reflect direct operations and purchased energy consumption for the reporting period.`
    );
  }, [
    data?.executiveSummary?.summaryOfFindings,
    data?.reportingYear,
    companyName,
  ]);
  useEffect(() => {
    setExecutiveSummaryText(defaultExecutiveSummaryText);
    setExecutiveSummaryOriginal(defaultExecutiveSummaryText);
  }, [defaultExecutiveSummaryText]);

  useEffect(() => {
    setOrganizationalBoundaryText(defaultOrganizationalBoundaryText);
    setOrganizationalBoundaryOriginal(defaultOrganizationalBoundaryText);
  }, [defaultOrganizationalBoundaryText]);

  useEffect(() => {
    setOperationalBoundaryText(defaultOperationalBoundaryText);
    setOperationalBoundaryOriginal(defaultOperationalBoundaryText);
  }, [defaultOperationalBoundaryText]);

  useEffect(() => {
    setEmissionFactorsText(defaultEmissionFactorsText);
    setEmissionFactorsOriginal(defaultEmissionFactorsText);
  }, [defaultEmissionFactorsText]);

  useEffect(() => {
    setQuantificationMethodologyText(defaultQuantificationMethodologyText);
    setQuantificationMethodologyOriginal(defaultQuantificationMethodologyText);
  }, [defaultQuantificationMethodologyText]);

  useEffect(() => {
    setDataAggregationText(defaultDataAggregationText);
    setDataAggregationOriginal(defaultDataAggregationText);
  }, [defaultDataAggregationText]);

  useEffect(() => {
    setAssumptionsText(defaultAssumptionsText);
    setAssumptionsOriginal(defaultAssumptionsText);
  }, [defaultAssumptionsText]);

  useEffect(() => {
    setLimitationsText(defaultLimitationsText);
    setLimitationsOriginal(defaultLimitationsText);
  }, [defaultLimitationsText]);

  useEffect(() => {
    setAnalysisText(defaultAnalysisText);
    setAnalysisOriginal(defaultAnalysisText);
  }, [defaultAnalysisText]);

  useEffect(() => {
    setVerificationText(defaultVerificationText);
    setVerificationOriginal(defaultVerificationText);
  }, [defaultVerificationText]);

  useEffect(() => {
    setConclusionsText(defaultConclusionsText);
    setConclusionsOriginal(defaultConclusionsText);
  }, [defaultConclusionsText]);
  const updateGhgReport = async (payload) => {
    if (!id) {
      throw new Error("GHG Report id is missing");
    }

    const response = await put(`/ghg-report/update?id=${id}`, payload);
    const updatedReport = response?.data?.data;

    if (updatedReport) {
      setData((prev) => ({
        ...prev,
        ...updatedReport,
      }));
    }

    return updatedReport;
  };

  const handleExecutiveSummarySave = async (text) => {
    await updateGhgReport({
      executiveSummary: {
        overallPerformance: text,
      },
    });

    setExecutiveSummaryText(text);
    setExecutiveSummaryOriginal(text);
    setData((prev) => ({
      ...prev,
      executiveSummary: {
        ...prev?.executiveSummary,
        overallPerformance: text,
      },
    }));
  };

  const handleOrganizationalBoundarySave = async (text) => {
    await updateGhgReport({
      executiveSummary: {
        organizationalBoundary: text,
      },
    });

    setOrganizationalBoundaryText(text);
    setOrganizationalBoundaryOriginal(text);
    setData((prev) => ({
      ...prev,
      executiveSummary: {
        ...prev?.executiveSummary,
        organizationalBoundary: text,
      },
    }));
  };

  const handleOperationalBoundarySave = async (text) => {
    await updateGhgReport({
      executiveSummary: {
        operationalBoundary: text,
      },
    });

    setOperationalBoundaryText(text);
    setOperationalBoundaryOriginal(text);
    setData((prev) => ({
      ...prev,
      executiveSummary: {
        ...prev?.executiveSummary,
        operationalBoundary: text,
      },
    }));
  };

  const handleEmissionFactorsSave = async (text) => {
    await updateGhgReport({
      emissionFactorAndDataSource: text,
    });

    setEmissionFactorsText(text);
    setEmissionFactorsOriginal(text);
    setData((prev) => ({
      ...prev,
      emissionFactorAndDataSource: text,
    }));
  };

  const handleQuantificationMethodologySave = async (text) => {
    await updateGhgReport({
      ghgQuntificationMethodology: text,
    });

    setQuantificationMethodologyText(text);
    setQuantificationMethodologyOriginal(text);
    setData((prev) => ({
      ...prev,
      ghgQuntificationMethodology: text,
    }));
  };

  const handleDataAggregationSave = async (text) => {
    await updateGhgReport({
      ghgDataAggregationAdnReportingPeriod: text,
    });

    setDataAggregationText(text);
    setDataAggregationOriginal(text);
    setData((prev) => ({
      ...prev,
      ghgDataAggregationAdnReportingPeriod: text,
    }));
  };

  const handleAssumptionsSave = async (text) => {
    await updateGhgReport({ assumptions: text });

    setAssumptionsText(text);
    setAssumptionsOriginal(text);
    setData((prev) => ({
      ...prev,
      assumptions: text,
    }));
  };

  const handleLimitationsSave = async (text) => {
    await updateGhgReport({ limitations: text });

    setLimitationsText(text);
    setLimitationsOriginal(text);
    setData((prev) => ({
      ...prev,
      limitations: text,
    }));
  };

  const handleAnalysisSave = async (text) => {
    await updateGhgReport({ analysis: text });

    setAnalysisText(text);
    setAnalysisOriginal(text);
    setData((prev) => ({
      ...prev,
      analysis: text,
    }));
  };

  const handleVerificationSave = async (text) => {
    await updateGhgReport({ verificationAndValidation: text });

    setVerificationText(text);
    setVerificationOriginal(text);
    setData((prev) => ({
      ...prev,
      verificationAndValidation: text,
    }));
  };

  const handleConclusionsSave = async (text) => {
    const executiveSummary = {
      ...(data?.executiveSummary || {}),
      summaryOfFindings: text,
    };

    await updateGhgReport({ executiveSummary });

    setConclusionsText(text);
    setConclusionsOriginal(text);
    setData((prev) => ({
      ...prev,
      executiveSummary: {
        ...(prev?.executiveSummary || {}),
        summaryOfFindings: text,
      },
    }));
  };
  const formatChangeLabel = (value) => (value >= 0 ? "increase" : "decrease");

  const getKeyFindingsList = () => {
    if (data?.executiveSummary?.keyFindings) {
      return data.executiveSummary.keyFindings
        .split("\n")
        .filter((item) => item.trim() !== "")
        .map((item) => item.trim());
    }

    const year = data?.reportingYear || new Date().getFullYear();
    const total = truncateEmissionNumber(getTotalEmissions() || 300321.04);
    const s1 = truncateEmissionNumber(scope1Total);
    const s2 = truncateEmissionNumber(scope2Total);
    const s3 = truncateEmissionNumber(scope3Total);

    return [
      `Total emissions for ${year} were calculated to be ${total} tCO2e`,
      `Scope 1 emissions recorded were ${s1} tCO2e, showing an ${formatChangeLabel(scope1Change)} of ${Math.abs(scope1Change).toFixed(2)}%`,
      `Scope 2 emissions were noted at ${s2} tCO2e, a ${formatChangeLabel(scope2Change)} of ${Math.abs(scope2Change).toFixed(2)}%`,
      `Scope 3 emissions surged to ${s3} tCO2e, a substantial ${formatChangeLabel(scope3Change)} of ${Math.abs(scope3Change).toFixed(2)}%`,
    ];
  };

  const getEmissionTrendsText = () => {
    if (data?.executiveSummary?.baselineComparison) {
      return data.executiveSummary.baselineComparison;
    }

    const year = data?.reportingYear || new Date().getFullYear();
    const total = truncateEmissionNumber(getTotalEmissions());

    return `${companyName} experienced its beginnings of emissions recording in ${year}, with total emissions reporting at ${total} tCO2e. Since this is the outset of its emissions calculation, and previous reports indicated zero emissions, year-on-year comparison cannot be established. Consequently, declaring a percentage growth in emissions from a prior non-reporting period is non-applicable.`;
  };

  const getGeneralInformationRows = () => {
    const generalInfo = data?.generalInformation || data?.generalInfo || {};

    return [
      {
        label: "Name of Reporting Organization :",
        value:
          generalInfo?.nameOfReportingOrganization ||
          generalInfo?.reportingOrganization ||
          data?.reportingOrganization ||
          companyName ||
          "ABC Engineering Solutions Pvt. Ltd.",
      },
      {
        label: "Organizational Boundary :",
        value:
          generalInfo?.organizationalBoundary ||
          data?.organizationalBoundary ||
          "Lorem Ipsum",
      },
      {
        label: "Description of Organization :",
        value:
          generalInfo?.descriptionOfOrganization ||
          generalInfo?.organizationDescription ||
          data?.organizationDescription ||
          user?.company?.companyDescription ||
          "ABC Engineering Solutions Pvt. Ltd. is an engineering company supplying and commissioning turnkey automated solutions. Their end-of-line packaging automation solutions.",
      },
      {
        label: "Website of Organization :",
        value:
          generalInfo?.websiteOfOrganization ||
          generalInfo?.website ||
          user?.company?.companyWebsite ||
          "loremipsum@abc.com",
      },
      {
        label: "Responsible Person :",
        value:
          generalInfo?.responsiblePerson ||
          data?.preparedBy?.name ||
          user?.name ||
          user?.company?.admin?.name ||
          "Lorem ipsum",
      },
      {
        label: "Contact Number :",
        value:
          generalInfo?.contactNumber ||
          generalInfo?.phone ||
          user?.phone ||
          user?.mobile ||
          user?.company?.phone ||
          "9999999999",
      },
      {
        label: "Email ID :",
        value:
          generalInfo?.emailId ||
          generalInfo?.email ||
          data?.preparedBy?.email ||
          user?.email ||
          user?.company?.admin?.email ||
          "loremipsum@abc.in",
      },
    ];
  };

  const scope1Headers = [
    "Emission Source",
    currentYearLabel,
    previousYearLabel,
    "% Change",
  ];

  const scope1Rows = [
    {
      "Emission Source": "Stationary Combustion",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.stationaryCombustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.stationaryCombustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope1Emissions?.stationaryCombustion?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Mobile Combustion",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.mobileCombustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.mobileCombustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope1Emissions?.mobileCombustion?.percentageChange ?? 0,
      ),
    },
    {
      "Emission Source": "Fugitive Emission",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.fugitiveCombustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.fugitiveCombustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope1Emissions?.fugitiveCombustion?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Total Scope 1 Emission",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope1Emissions?.totalScope1Combustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope1Emissions?.totalScope1Combustion?.percentageChange ?? 0,
        2,
      ),
    },
  ];

  const scope2Rows = [
    {
      "Emission Source": "Electricity",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.electricity?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.electricity?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope2Emissions?.electricity?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Heating and Cooling",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.heat?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.heat?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope2Emissions?.heat?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Steam",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.steam?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.steam?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope2Emissions?.steam?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Total Scope 2 Emission",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope2Emissions?.totalEmission?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope2Emissions?.totalEmission?.percentageChange ?? 0,
        2,
      ),
    },
  ];

  const scope3Rows = [
    {
      "Emission Source": "Purchased Goods & Services",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.purchasedGoods?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]:
        truncateEmissionNumber(
          data?.scope3Emissions?.purchasedGoods?.previousMonthTotal,
        ) ?? 0,
      "% Change": truncateEmissionNumber(
        data?.scope3Emissions?.purchasedGoods?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Upstream/Downstream Transportation & Distribution",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Waste Generated in Operations",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.wasteGenerated?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.wasteGenerated?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope3Emissions?.wasteGenerated?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Employee Commute",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.employeeCommute?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.employeeCommute?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope3Emissions?.employeeCommute?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Business Travel",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.businessTravel?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.businessTravel?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope3Emissions?.businessTravel?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Total Scope 3 Emission",
      [currentYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateEmissionNumber(
        data?.scope3Emissions?.totalEmission?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateEmissionNumber(
        data?.scope3Emissions?.totalEmission?.percentageChange ?? 0,
        2,
      ),
    },
  ];

  let scope1Data = [
    {
      label: `${currentYearDisplay}`,
      data: scope1BarChartData?.map((item) => item.thisYearEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      // borderWidth: 1,
      barThickness: 30,
    },
    {
      label: `${previousYearDisplay}`,
      data: scope1BarChartData?.map((item) => item.previousEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      // borderWidth: 1,
      barThickness: 30,
    },
  ];

  let scope2Data = [
    {
      label: `${currentYearDisplay}`,
      data: scope2BarChartData?.map((item) => item.thisYearEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: `${previousYearDisplay}`,
      data: scope2BarChartData?.map((item) => item.previousEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let scope3Data = [
    {
      label: `${currentYearDisplay}`,
      data: scope3BarChartData?.map((item) => item.thisYearEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: `${previousYearDisplay}`,
      data: scope3BarChartData?.map((item) => item.previousEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  const open = Boolean(anchorEl);

  const downloadReport = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Helper function to convert image to base64 data URL
  const loadImageAsDataUrl = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  // Helper function to convert SVG to PNG data URL with explicit dimensions
  const convertSvgToPng = (
    svgSrc,
    width = 200,
    height = 60,
    transparentBackground = false,
  ) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          if (!transparentBackground) {
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, width, height);
          }
          // Draw the SVG image scaled to fit
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/png"));
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };
      img.onerror = (e) => {
        console.error("Error loading SVG:", e);
        reject(new Error("Failed to load SVG image"));
      };
      img.src = svgSrc;
    });
  };

  // Helper function to convert all images in HTML to base64
  const convertImagesToBase64 = async (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    for (const img of Array.from(images)) {
      const src = img.getAttribute("src");
      if (src && !src.startsWith("data:")) {
        try {
          const dataUrl = await loadImageAsDataUrl(src);
          img.setAttribute("src", dataUrl);
        } catch (error) {
          console.warn(`Could not convert image ${src} to base64:`, error);
        }
      }
    }

    return doc.documentElement.outerHTML;
  };

  /** A4 bleed into backend puppeteer margins — frontend-only PDF cover fix */
  const applyGhgCoverFullPageForPdf = (htmlContent) => {
    const { top, right, bottom, left } = GHG_PDF_BACKEND_MARGINS;
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div id="ghg-pdf-root">${htmlContent}</div>`,
      "text/html",
    );
    const root = doc.getElementById("ghg-pdf-root");
    if (!root) return htmlContent;

    const cover = root.querySelector('[data-ghg-pdf-cover-page="true"]');
    if (!cover) return htmlContent;

    const wrapper = doc.createElement("div");
    wrapper.setAttribute("data-ghg-pdf-cover-wrapper", "true");
    cover.parentNode.insertBefore(wrapper, cover);
    wrapper.appendChild(cover);

    wrapper.style.boxSizing = "border-box";
    wrapper.style.position = "relative";
    wrapper.style.width = "210mm";
    wrapper.style.height = "297mm";
    wrapper.style.margin = `-${top} -${right} -${bottom} -${left}`;
    wrapper.style.padding = "0";
    wrapper.style.overflow = "hidden";
    wrapper.style.borderRadius = "0";

    const bgImg = cover.querySelector('img[class*="cover_bg"]');
    const bgSrc = bgImg?.getAttribute("src")?.trim();

    cover.style.width = "100%";
    cover.style.height = "100%";
    cover.style.margin = "0";
    cover.style.padding = "0";
    cover.style.borderRadius = "0";
    cover.style.overflow = "hidden";
    cover.style.position = "relative";
    cover.style.boxSizing = "border-box";

    if (bgSrc) {
      cover.style.backgroundImage = `url("${bgSrc}")`;
      cover.style.backgroundSize = "cover";
      cover.style.backgroundPosition = "center center";
      cover.style.backgroundRepeat = "no-repeat";
    }

    if (bgImg) {
      bgImg.style.position = "absolute";
      bgImg.style.top = "0";
      bgImg.style.left = "0";
      bgImg.style.width = "100%";
      bgImg.style.height = "100%";
      bgImg.style.objectFit = "cover";
      bgImg.style.objectPosition = "center center";
      bgImg.style.maxWidth = "none";
      bgImg.style.display = "block";
      bgImg.style.zIndex = "0";
    }

    const overlay = cover.querySelector('div[class*="cover_overlay"]');
    if (overlay) {
      overlay.style.position = "absolute";
      overlay.style.top = "0";
      overlay.style.left = "0";
      overlay.style.width = "100%";
      overlay.style.height = "100%";
      overlay.style.zIndex = "1";
    }

    const content = cover.querySelector('div[class*="cover_content"]');
    if (content) {
      content.style.position = "absolute";
      content.style.top = "0";
      content.style.left = "0";
      content.style.width = "100%";
      content.style.height = "100%";
      content.style.zIndex = "2";
      content.style.boxSizing = "border-box";
    }

    return root.innerHTML;
  };

  const downloadReportPdf = async () => {
    handleClose();
    try {
      const element = reportRef.current;

      if (!element) {
        console.error("Report container not found");
        return;
      }

      // Load logo image as data URL for header
      // Backend will use fallback if logoDataUrl is not provided
      let logoDataUrl;
      let vajraLogoDataUrl;
      try {
        vajraLogoDataUrl = await loadImageAsDataUrl(vajraLogo);
      } catch (error) {
        console.warn("Could not load Vajra logo image:", error);
      }

      // White-label the running page header's left logo too (consultantLogoUrl
      // is computed at component scope). Falls back to the Vajra logo above.
      if (consultantLogoUrl) {
        try {
          vajraLogoDataUrl = await loadImageAsDataUrl(consultantLogoUrl);
        } catch (error) {
          console.warn("Consultant logo load failed, using Vajra default", error);
        }
      }

      try {
        const logoSrc = user?.company?.companyImage;
        if (logoSrc) {
          logoDataUrl = await loadImageAsDataUrl(logoSrc);
        }
      } catch (error) {
        console.warn("Could not load logo image:", error);
        // Backend will use fallback logo if this fails
      }

      // Send footer logo to backend as a base64 data URL.
      let poweredByLogoDataUrl;
      try {
        poweredByLogoDataUrl = await convertSvgToPng(
          poweredByLogo,
          200,
          60,
          true,
        );
      } catch (error) {
        console.error("Could not load powered by logo:", error);
      }

      // Wait for charts to fully render
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Convert all canvas elements (charts) to images before cloning
      const canvases = Array.from(element.querySelectorAll("canvas"));
      const canvasDataUrls = [];

      for (const canvas of canvases) {
        try {
          // Get canvas data URL with high quality
          const dataUrl = canvas.toDataURL("image/png", 1.0);

          // Get dimensions
          const width = canvas.width || canvas.offsetWidth || 400;
          const height = canvas.height || canvas.offsetHeight || 300;

          canvasDataUrls.push({
            dataUrl,
            width,
            height,
            styleWidth: canvas.style.width || `${width}px`,
            styleHeight: canvas.style.height || `${height}px`,
          });
        } catch (error) {
          console.warn("Could not convert canvas to image:", error);
          canvasDataUrls.push(null);
        }
      }

      // Clone the element after charts are rendered
      const clonedElement = element.cloneNode(true);
      const duplicatedHeaderLogoClasses = [
        coverStyles.toc_header_logo,
        coverStyles.exec_header_logo,
        coverStyles.report_page_logo,
      ].filter(Boolean);
      clonedElement.querySelectorAll("img").forEach((logo) => {
        if (
          duplicatedHeaderLogoClasses.some((className) =>
            logo.classList.contains(className),
          )
        ) {
          const logoWrapper = logo.parentElement;
          logo.remove();
          if (logoWrapper && !logoWrapper.textContent.trim()) {
            logoWrapper.remove();
          }
        }
      });
      clonedElement.style.padding = "0";
      clonedElement.style.margin = "0";

      // Cover/TOC are hidden on screen only — ensure they appear in PDF
      const hidePageClass = coverStyles.hidepage;
      clonedElement
        .querySelectorAll(`[class~="${hidePageClass}"]`)
        .forEach((el) => el.classList.remove(hidePageClass));

      clonedElement
        .querySelectorAll("[data-ghg-report-edit-ui]")
        .forEach((el) => el.remove());

      // Replace canvas elements with img elements in cloned element
      const clonedCanvases = Array.from(
        clonedElement.querySelectorAll("canvas"),
      );
      clonedCanvases.forEach((clonedCanvas, index) => {
        const imageData = canvasDataUrls[index];
        if (imageData && imageData.dataUrl) {
          const img = document.createElement("img");
          img.setAttribute("src", imageData.dataUrl); // Use setAttribute to ensure it's in HTML
          img.style.width = imageData.styleWidth;
          img.style.height = imageData.styleHeight;
          img.style.maxWidth = "100%";
          img.style.display = "block";
          img.setAttribute("alt", "Chart");

          // Replace canvas with img
          if (clonedCanvas.parentNode) {
            clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
          }
        }
      });

      // Remove download dropdown from cloned element
      const downloadDropdown =
        clonedElement.querySelector(".download-dropdown");
      if (downloadDropdown) {
        downloadDropdown.remove();
      }

      // Remove "Powered by" footer section from cloned element (for PDF only)
      // Note: This only removes from clonedElement, not the original element
      // The footer remains visible in the regular view, only removed in PDF download
      const poweredBySection =
        clonedElement.querySelector("#company_footer") ||
        clonedElement.querySelector(".company_footer");
      if (poweredBySection) {
        poweredBySection.remove();
      }

      // Convert all images in the HTML to base64 (including the chart images we just created)
      let htmlContent = clonedElement.innerHTML;
      htmlContent = await convertImagesToBase64(`<div>${htmlContent}</div>`);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      htmlContent = doc.body.innerHTML;
      htmlContent = applyGhgCoverFullPageForPdf(htmlContent);

      /* First-page cover CSS must be last so it overrides module/legacy rules */
      const cssStyles = `${getGhgReportPdfStyles()}\n${getGhgReportFirstPagePdfCss()}`;

      // Get FULL HTML of the report component with all CSS styles
      const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
          />
          <style>
            ${cssStyles}
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;

      // Get base URL and remove /api/v1 suffix for PDF generation endpoint
      const apiBaseUrl =
        sessionStorage.getItem("apiBaseUrl") ||
        api ||
        "https://uat-api.nirantara.ai";
      const baseUrl = apiBaseUrl.replace(/\/api\/v1$/, "");

      const response = await fetch(`${baseUrl}/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          html,
          css: cssStyles,
          showReportHeader: true,
          leftLogoDataUrl: vajraLogoDataUrl || undefined,
          companyImage: logoDataUrl || user?.company?.companyImage || undefined,
          logoDataUrl: logoDataUrl || user?.company?.companyImage || undefined,
          skipFirstPageHeader: true,
          poweredByLogoDataUrl: poweredByLogoDataUrl || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      // Convert response to Blob and download
      const pdfBlob = await response.blob();
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "GHG_Report.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF download error:", error);
    }
  };

  const downloadReportWord = () => {
    handleClose();
    const input = reportRef.current;
    if (!input) {
      console.error("❌ reportRef is null");
      return;
    }

    const buildWordHtml = () => {
      const clone = input.cloneNode(true);
      const sourceCanvases = input.querySelectorAll("canvas");
      const cloneCanvasList = [...clone.querySelectorAll("canvas")];

      sourceCanvases.forEach((sourceCanvas, index) => {
        const target = cloneCanvasList[index];
        if (!target || !(sourceCanvas instanceof HTMLCanvasElement)) return;
        const exported = chartCanvasForWordExport(sourceCanvas, 640);
        if (!exported) return;
        try {
          const img = document.createElement("img");
          img.src = exported.dataUrl;
          img.alt = "Chart";
          img.setAttribute("width", String(exported.width));
          img.setAttribute("height", String(exported.height));
          target.replaceWith(img);
        } catch (err) {
          console.warn("Could not export chart canvas to Word:", err);
        }
      });

      let htmlContent = clone.innerHTML;

      const pageBreakHTML = "<br style='page-break-before: always;'>";

      const tables = input.querySelectorAll("table");
      tables.forEach((table) => {
        if (table.scrollHeight > 800) {
          htmlContent = htmlContent.replace(
            table.outerHTML,
            pageBreakHTML + table.outerHTML,
          );
        }
      });

      htmlContent = htmlContent.replace(
        /(<h3>Scope \d+:.*?<\/h3>)/g,
        `${pageBreakHTML}$1`,
      );

      const header = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office'
            xmlns:w='urn:schemas-microsoft-com:office:word'
            xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>Document</title>
          <style>
            table {
              font-size: 12px;
              border-collapse: collapse;
              width: 100%;
              page-break-inside: avoid; /* Avoid splitting table */
            }
            thead {
              display: table-header-group;
            }
            tfoot {
              display: table-footer-group;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            td, th {
              border: 1px solid #000;
              padding: 4px;
              text-align: left;
            }
          </style>
        </head>
        <body>
    `;

      const footer = "</body></html>";

      const sourceHTML = header + htmlContent + footer;

      const blob = new Blob(["\ufeff", sourceHTML], {
        type: "application/msword",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "GHG_Report.doc";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(buildWordHtml);
    });
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "1.5rem",
        }}
      >
        <p
          style={{
            fontFamily: "Inter",
            fontSize: "22px",
            fontWeight: "700",
          }}
        >
          GHG Report Details
        </p>
        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#1C486B",
            padding: "0.5rem 1rem",
            borderRadius: "4px",
            height: "40px",
            textTransform: "none",
          }}
          onClick={downloadReport}
        >
          Download Report
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={downloadReportPdf}>Download as PDF</MenuItem>
          <MenuItem onClick={downloadReportWord}>Download as Word</MenuItem>
        </Menu>
      </div>

      <div ref={reportRef} style={{ padding: "1rem 0" }}>
        {/* Cover Page */}
        <section
          className={`${coverStyles.cover_page} ${coverStyles.hidepage}`}
          data-ghg-pdf-cover-page="true"
        >
          <img
            src={ghgCoverBg}
            alt=""
            className={coverStyles.cover_bg}
            aria-hidden="true"
          />
          <div className={coverStyles.cover_overlay} aria-hidden="true" />
          <div className={coverStyles.cover_content}>
            <div className={coverStyles.cover_header}>
              <img
                src={platformLogoTransparent}
                alt="Vajra Logo"
                className={coverStyles.cover_company_logo}
              />
              <div className={coverStyles.cover_client_logo_wrap}>
                <img
                  src={companyLogoSrc || companyLogo}
                  alt="Company Logo"
                  className={coverStyles.cover_client_logo}
                />
              </div>
            </div>

            <div className={coverStyles.cover_body}>
              <h1 className={coverStyles.cover_title}>GHG REPORT</h1>
              <div className={coverStyles.cover_color_bar}>
                <span className={coverStyles.cover_bar_green} />
                <span className={coverStyles.cover_bar_orange} />
                <span className={coverStyles.cover_bar_blue} />
              </div>
              <div className={coverStyles.cover_divider} />

              <div className={coverStyles.cover_info_grid}>
                <div className={coverStyles.cover_info_item}>
                  <span className={coverStyles.cover_info_label}>
                    Company Name
                  </span>
                  <span className={coverStyles.cover_info_value}>
                    {user?.company?.companyName || "-"}
                  </span>
                </div>
                <div className={coverStyles.cover_info_item}>
                  <span className={coverStyles.cover_info_label}>
                    Reporting Period
                  </span>
                  <span className={coverStyles.cover_info_value}>
                    {getReportingPeriodLabel()}
                  </span>
                </div>
                <div className={coverStyles.cover_info_item}>
                  <span className={coverStyles.cover_info_label}>Date</span>
                  <span className={coverStyles.cover_info_value}>
                    {getReportDateLabel()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className={`${coverStyles.toc_page} ${coverStyles.hidepage}`}>
          <img
            src={platformLogo}
            alt="Vajra Logo"
            className={coverStyles.toc_header_logo}
          />
          <h2 className={coverStyles.toc_title}>TABLE OF CONTENTS</h2>
          <div className={coverStyles.toc_color_bar}>
            <span className={coverStyles.cover_bar_green} />
            <span className={coverStyles.cover_bar_orange} />
            <span className={coverStyles.cover_bar_blue} />
          </div>
          <ul className={coverStyles.toc_list}>
            {GHG_TABLE_OF_CONTENTS.map((item) => (
              <li
                key={item}
                className={`${coverStyles.toc_item} ${
                  item === "Executive Summary" || item === "Appendices"
                    ? coverStyles.toc_item_unnumbered
                    : ""
                }`}
              >
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Executive Summary Intro Page */}
        <section className={coverStyles.exec_page}>
          <img
            src={platformLogo}
            alt="Vajra Logo"
            className={coverStyles.exec_header_logo}
          />
          <h1 className={coverStyles.exec_main_title}>
            Greenhouse Gas Inventory Report
          </h1>

          <div className={coverStyles.exec_info_box}>
            <div className={coverStyles.exec_info_details}>
              <div className={coverStyles.exec_info_row}>
                <span className={coverStyles.exec_info_label}>
                  Company Name:
                </span>
                <span className={coverStyles.exec_info_value}>
                  {companyName}
                </span>
              </div>
              <div className={coverStyles.exec_info_row}>
                <span className={coverStyles.exec_info_label}>
                  Inventory Year:
                </span>
                <span className={coverStyles.exec_info_value}>
                  {getReportingPeriodLabel()}
                </span>
              </div>
              <div className={coverStyles.exec_info_row}>
                <span className={coverStyles.exec_info_label}>
                  Date of report generation:
                </span>
                <span className={coverStyles.exec_info_value}>
                  {getReportDateLabel()}
                </span>
              </div>
            </div>
            <div className={coverStyles.exec_info_logo_wrap}>
              <img
                src={companyLogoSrc || companyLogo}
                alt="Company Logo"
                className={coverStyles.exec_info_logo}
              />
            </div>
          </div>

          <EditableReportSection
            title="Executive Summary"
            value={getExecutiveSummaryText()}
            originalValue={executiveSummaryOriginal}
            onSave={handleExecutiveSummarySave}
            contentClassName={coverStyles.exec_paragraph}
            showColorBar={false}
            showTitle={true}
          />

          <div className={coverStyles.exec_highlight_box}>
            <h3 className={coverStyles.exec_highlight_title}>Key findings:</h3>
            <ul className={coverStyles.exec_list}>
              {getKeyFindingsList().map((finding, index) => (
                <li key={index} className={coverStyles.exec_list_item}>
                  {finding}
                </li>
              ))}
            </ul>
          </div>

          <div className={coverStyles.exec_highlight_box}>
            <h3 className={coverStyles.exec_highlight_title}>
              Emission trends:
            </h3>
            <ul className={coverStyles.exec_list}>
              <li className={coverStyles.exec_list_item}>
                {getEmissionTrendsText()}
              </li>
            </ul>
          </div>
        </section>

        {/* General Information Page */}
        <section className={coverStyles.general_page}>
          <img
            src={platformLogo}
            alt="Vajra Logo"
            className={coverStyles.report_page_logo}
          />
          <h2 className={coverStyles.general_title}>1. General information</h2>
          <div className={coverStyles.general_table}>
            {getGeneralInformationRows().map((row) => (
              <div key={row.label} className={coverStyles.general_row}>
                <span className={coverStyles.general_label}>{row.label}</span>
                <span className={coverStyles.general_value}>{row.value}</span>
              </div>
            ))}
          </div>
        </section>

        <GhgReportStyledPages
          PageBreak={PageBreak}
          styles={styles}
          coverStyles={coverStyles}
          vajraLogo={platformLogo}
          companyLogo={poweredByLogo}
          companyName={companyName}
          data={data}
          summary={summary}
          getReportingPeriodLabel={getReportingPeriodLabel}
          getExecutiveSummaryText={getExecutiveSummaryText}
          organizationalBoundaryText={organizationalBoundaryText}
          organizationalBoundaryOriginal={organizationalBoundaryOriginal}
          onOrganizationalBoundarySave={handleOrganizationalBoundarySave}
          operationalBoundaryText={operationalBoundaryText}
          operationalBoundaryOriginal={operationalBoundaryOriginal}
          onOperationalBoundarySave={handleOperationalBoundarySave}
          emissionFactorsText={emissionFactorsText}
          emissionFactorsOriginal={emissionFactorsOriginal}
          onEmissionFactorsSave={handleEmissionFactorsSave}
          quantificationMethodologyText={quantificationMethodologyText}
          quantificationMethodologyOriginal={quantificationMethodologyOriginal}
          onQuantificationMethodologySave={handleQuantificationMethodologySave}
          dataAggregationText={dataAggregationText}
          dataAggregationOriginal={dataAggregationOriginal}
          onDataAggregationSave={handleDataAggregationSave}
          assumptionsText={assumptionsText}
          assumptionsOriginal={assumptionsOriginal}
          onAssumptionsSave={handleAssumptionsSave}
          limitationsText={limitationsText}
          limitationsOriginal={limitationsOriginal}
          onLimitationsSave={handleLimitationsSave}
          analysisText={analysisText}
          analysisOriginal={analysisOriginal}
          onAnalysisSave={handleAnalysisSave}
          verificationText={verificationText}
          verificationOriginal={verificationOriginal}
          onVerificationSave={handleVerificationSave}
          conclusionsText={conclusionsText}
          conclusionsOriginal={conclusionsOriginal}
          onConclusionsSave={handleConclusionsSave}
          getTotalEmissions={getTotalEmissions}
          currentYearDisplay={currentYearDisplay}
          previousYearDisplay={previousYearDisplay}
          scope1Headers={scope1Headers}
          scope1Rows={scope1Rows}
          scope2Rows={scope2Rows}
          scope3Rows={scope3Rows}
          scope1BarChartData={scope1BarChartData}
          scope2BarChartData={scope2BarChartData}
          scope3BarChartData={scope3BarChartData}
          scope1Data={scope1Data}
          scope2Data={scope2Data}
          scope3Data={scope3Data}
          truncateEmissionNumber={truncateEmissionNumber}
        />
      </div>
      {/* <div className={coverStyles.fotter}>
            <p>jsjsjssjsj</p>
      </div> */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>
          We are comparing GHG Report. Please wait...
        </Typography>
      </Backdrop>
    </>
  );
};

export default GhgReport;
